import { gql } from "@apollo/client";
import { memo, useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Alert } from "swash/Alert";
import { Button } from "swash/Button";
import { SegmentedControl } from "swash/controls/SegmentedControl";

import { ForecastInsightsChart } from "@/components/charts/ForecastInsightsChart";
import { useAmplitude } from "@/containers/Amplitude";
import { useSafeQuery } from "@/containers/Apollo";
import { ErrorBoundary } from "@/containers/ErrorBoundary";
import { HasPermission } from "@/containers/User";
import { omitTypename } from "@/services/utils";

import { AnalyticsGroup } from "./Group";

const ArticleQuery = gql`
  query AnalyticsPanelForecastInsights_article(
    $id: Int!
    $input: ForecastInsightsInput!
  ) {
    article(id: $id) {
      id
      forecastInsights(input: $input) {
        conversions {
          date
          app
          mobile
          desktop
        }
      }
    }
  }
`;

const conversionsConfig = {
  yLimit: [0, "dataMax"],
  height: 140,
  animationDuration: 500,
  labels: [
    {
      key: "app",
      color: "#FF6B6E",
      tailwind: "border-red-300 bg-red-300/20",
      legend: "App",
      hint: "Sed gravida lectus ut purus. Morbi laoreet magna. Pellentesque eu wisi...",
    },
    {
      key: "desktop",
      color: "#5849E2",
      tailwind: "border-purple-600 bg-purple-600/20",
      legend: "Web (desktop)",
      hint: "Sed gravida lectus ut purus. Morbi laoreet magna. Pellentesque eu wisi...",
    },
    {
      key: "mobile",
      color: "#42C798",
      tailwind: "border-green-300 bg-green-300/20",
      legend: "Web (mobile)",
      hint: "Sed gravida lectus ut purus. Morbi laoreet magna. Pellentesque eu wisi...",
    },
  ],
};

function useLogEventPeriodChange() {
  const { logEvent } = useAmplitude();
  const { pathname } = useLocation();

  const source = useMemo(() => pathname.split("/")[1], [pathname]);

  return useCallback(
    ({ period, chart }) =>
      logEvent("statsPanel:chart:changePeriod", {
        source,
        chart,
        period,
      }),
    [logEvent, source],
  );
}

function Conversions({ articleId, config }) {
  const [period, setPeriod] = useState("PERIOD_LAST_24_HOURS");
  const logEventPeriodChange = useLogEventPeriodChange();
  const { data, loading } = useSafeQuery(ArticleQuery, {
    variables: { id: articleId, input: { period } },
    fetchPolicy: "network-only",
  });

  const conversions = useMemo(() => {
    if (!data) return [];
    const conversions = data?.article?.forecastInsights?.conversions;
    return conversions.map((conversion) => omitTypename(conversion));
  }, [data]);

  // @todo: this is a mock use data from forecast API
  const totalConversions = useMemo(() => {
    if (!conversions?.length) return [];
    const initialMap = config.labels.reduce((acc, label) => {
      acc[label.key] = 0;
      return acc;
    }, {});
    const keys = config.labels.map(({ key }) => key);

    const total = conversions.reduce((acc, item) => {
      Object.entries(item)
        .filter(([key]) => keys.includes(key))
        .forEach(([key, value]) => {
          acc[key] += value;
        });
      return acc;
    }, initialMap);
    return [total];
  }, [conversions, config.labels]);

  const setChartPeriod = useCallback(
    (period) => {
      logEventPeriodChange({ chart: "conversions", period });
      setPeriod(period);
    },
    [logEventPeriodChange],
  );

  return (
    <AnalyticsGroup title="Conversions" value={237}>
      <SegmentedControl
        className="my-2"
        value={period}
        scale="sm"
        onChange={setChartPeriod}
      >
        <Button id="PERIOD_LAST_24_HOURS">24 heures</Button>
        <Button id="PERIOD_LAST_7_DAYS">7 jours</Button>
        <Button id="PERIOD_LAST_30_DAYS">30 jours</Button>
        <Button id="PERIOD_SINCE_PUBLICATION">Total</Button>
      </SegmentedControl>
      <ForecastInsightsChart
        data={
          period !== "PERIOD_SINCE_PUBLICATION" ? conversions : totalConversions
        }
        config={conversionsConfig}
        period={period}
        loading={loading}
      />
    </AnalyticsGroup>
  );
}

const ForecastEnabledQuery = gql`
  query ArticleAnalyticsPanel_thirdPartyService {
    thirdPartyService(key: forecast) {
      enabled
    }
  }
`;

const MissingForecastData = memo(() => {
  return (
    <Alert>
      Les données provenant de Forecast sont incomplètes et ne peuvent être
      affichées.
    </Alert>
  );
});

export function ForecastInsights({ articleId }) {
  const { data } = useSafeQuery(ForecastEnabledQuery);
  if (!data) return null;
  return (
    <HasPermission permission="stats:full">
      {data.thirdPartyService.enabled && (
        <ErrorBoundary
          component={MissingForecastData}
          shouldCaptureError={() => false}
        >
          <Conversions articleId={articleId} config={conversionsConfig} />
        </ErrorBoundary>
      )}
    </HasPermission>
  );
}
